"use client"

import {
    AlertTriangle,
    BarChart3,
    BookOpen,
    CalendarClock,
    Clock,
    GraduationCap,
    PackageX,
    PauseCircle,
    ShieldAlert,
} from "lucide-react"

import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/common/components/ui/select"
import { cn } from "@/common/lib/utils"
import { CAST } from "@/modules/lernpfad/data/cast"
import type { LernpfadDemoState } from "@/app/demo/lernpfad/use-lernpfad-demo"
import GapDumbbellChart from "./charts/gap-dumbbell-chart"
import QuarterLoadChart from "./charts/quarter-load-chart"
import CurriculumItemCard from "./curriculum-item-card"
import { SectionHeading, Stat } from "./ui-bits"
import { ViewHeader } from "@/modules/lernpfad/components/ui-bits"

export default function EmployeeView({ state }: { state: LernpfadDemoState }) {
    const {
        employee,
        employeeId,
        selectEmployee,
        curriculum,
        gaps,
        asOf,
        markDone,
        extraCompleted,
        localizer,
        rationaleStrings,
        moodleConnected,
        connections,
        lmsCompletions,
        openLms,
    } = state
    const { t } = localizer
    const ltiDone = new Set(
        lmsCompletions.filter((entry) => entry.via === "lti").map((entry) => entry.bausteinId),
    )

    const mandatoryCount = curriculum.items.filter((item) => item.reason === "pflicht").length
    const overdueCount = curriculum.items.filter(
        (item) => item.dueBy != null && item.dueBy < asOf,
    ).length

    return (
        <div className="space-y-8">
            {/* Identity card */}
            <ViewHeader
                title={t("employee.title")}
                subtitle={
                    <>
                        {employee.displayName} ·{" "}
                        {localizer.role(employee.roleId, employee.roleLabel)} ·{" "}
                        {employee.orgUnitLabel} · {employee.pensum}%
                    </>
                }
                lead={
                    <span className="flex size-12 shrink-0 items-center justify-center rounded-full bg-primary/10 text-base font-semibold text-primary">
                        {employee.displayName
                            .split(" ")
                            .map((part) => part[0])
                            .slice(0, 2)
                            .join("")}
                    </span>
                }
                action={
                    <Select value={employeeId} onValueChange={selectEmployee}>
                    <SelectTrigger className="w-[290px] bg-card">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        {CAST.map((member) => (
                            <SelectItem key={member.id} value={member.id}>
                                {member.displayName} — {localizer.role(member.roleId, member.roleLabel)}
                            </SelectItem>
                        ))}
                        </SelectContent>
                    </Select>
                }
            >
                <p className="mt-1 text-xs text-muted-foreground">
                    {moodleConnected
                        ? t("src.catalogLine", { count: connections.moodle?.syncedCourses ?? 0 })
                        : t("src.demoData")}
                </p>
            </ViewHeader>

            {/* Headline numbers */}
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                <Stat
                    icon={BookOpen}
                    label={t("employee.stat.items")}
                    value={String(curriculum.items.length)}
                />
                <Stat
                    icon={Clock}
                    label={t("employee.stat.hours")}
                    value={t("item.hours", { hours: curriculum.totalHours })}
                />
                <Stat
                    icon={ShieldAlert}
                    label={t("employee.stat.mandatory")}
                    value={String(mandatoryCount)}
                    tone={mandatoryCount > 0 ? "warn" : "neutral"}
                />
                <Stat
                    icon={AlertTriangle}
                    label={t("employee.stat.overdue")}
                    value={String(overdueCount)}
                    tone={overdueCount > 0 ? "danger" : "neutral"}
                />
            </div>

            {overdueCount > 0 && (
                <div className="flex items-start gap-3 rounded-lg border-l-4 border-l-destructive border-y border-r bg-destructive/5 p-4">
                    <AlertTriangle className="mt-0.5 size-5 shrink-0 text-destructive" />
                    <div className="text-sm">
                        <p className="font-semibold text-destructive">
                            {t(
                                overdueCount === 1
                                    ? "employee.overdueTitle"
                                    : "employee.overdueTitle.plural",
                                { count: overdueCount },
                            )}
                        </p>
                        <p className="mt-1 text-muted-foreground">{t("employee.overdueBody")}</p>
                    </div>
                </div>
            )}

            {/* Load per quarter */}
            <section className="lp-card rounded-xl border bg-card p-5">
                <SectionHeading icon={BarChart3} title={t("employee.load")} />
                <QuarterLoadChart
                    curriculum={curriculum}
                    t={t}
                    reasonLabel={localizer.reason}
                />
            </section>

            {/* The plan, by quarter */}
            <div className="space-y-6">
                {curriculum.quarters.map((quarter) => {
                    if (quarter.items.length === 0) return null
                    const overBudget = quarter.plannedHours > quarter.budgetHours
                    return (
                        <section key={quarter.index}>
                            <div className="mb-3 flex items-center justify-between border-b pb-2">
                                <h3 className="flex items-center gap-2 font-semibold">
                                    <CalendarClock className="size-4 text-muted-foreground" />
                                    {quarter.label}
                                </h3>
                                <span
                                    className={cn(
                                        "rounded-full px-2.5 py-0.5 text-xs tabular-nums",
                                        overBudget
                                            ? "bg-destructive/10 font-semibold text-destructive"
                                            : "bg-muted text-muted-foreground",
                                    )}
                                >
                                    {t("employee.quarterBudget", {
                                        planned: quarter.plannedHours,
                                        budget: quarter.budgetHours,
                                    })}
                                    {overBudget && ` — ${t("employee.overBudget")}`}
                                </span>
                            </div>
                            <div className="space-y-2.5">
                                {quarter.items.map((item) => (
                                    <CurriculumItemCard
                                        key={`${quarter.index}-${item.baustein.id}`}
                                        item={item}
                                        employee={employee}
                                        asOf={asOf}
                                        localizer={localizer}
                                        rationaleStrings={rationaleStrings}
                                        onMarkDone={markDone}
                                        done={
                                            extraCompleted.includes(item.baustein.id) ||
                                            ltiDone.has(item.baustein.id)
                                        }
                                        moodleConnected={moodleConnected}
                                        onLaunch={(baustein) => openLms(baustein.id)}
                                        viaLti={ltiDone.has(item.baustein.id)}
                                    />
                                ))}
                            </div>
                        </section>
                    )
                })}
            </div>

            {/* Deferred — never silently dropped */}
            {curriculum.deferred.length > 0 && (
                <section>
                    <SectionHeading
                        icon={PauseCircle}
                        title={t("employee.deferred")}
                        count={curriculum.deferred.length}
                    />
                    <p className="mb-3 text-sm text-muted-foreground">{t("employee.deferredHint")}</p>
                    <div className="space-y-1.5">
                        {curriculum.deferred.map((item) => (
                            <div
                                key={item.baustein.id}
                                className="flex items-center justify-between gap-3 rounded-md border border-dashed bg-muted/25 px-3 py-2 text-sm"
                            >
                                <span className="truncate">{localizer.baustein(item.baustein).title}</span>
                                <span className="shrink-0 tabular-nums text-muted-foreground">
                                    {t("item.hours", { hours: item.baustein.durationHours })}
                                </span>
                            </div>
                        ))}
                    </div>
                </section>
            )}

            {/* Catalog gaps */}
            {curriculum.uncoveredGaps.length > 0 && (
                <section className="rounded-lg border border-dashed p-4">
                    <div className="mb-1.5 flex items-center gap-2">
                        <PackageX className="size-4 text-muted-foreground" />
                        <h3 className="text-sm font-semibold">{t("employee.noCatalog")}</h3>
                    </div>
                    <p className="text-sm text-muted-foreground">
                        {curriculum.uncoveredGaps
                            .map((gap) => localizer.skill(gap.skillId, gap.skillLabel))
                            .join(", ")}
                    </p>
                </section>
            )}

            {/* Gap chart — the working-out behind the plan */}
            <section className="lp-card rounded-xl border bg-card p-5">
                <SectionHeading
                    icon={GraduationCap}
                    title={t("employee.gapTable")}
                    count={gaps.length}
                />
                {gaps.length > 0 ? (
                    <GapDumbbellChart
                        gaps={gaps}
                        t={t}
                        skillLabel={localizer.skill}
                        levelLabel={localizer.level}
                        originLabel={localizer.origin}
                    />
                ) : (
                    <p className="text-sm text-muted-foreground">{t("manager.noAction")}</p>
                )}

                {/* Evidence, only where a source supplied one. */}
                {gaps.some((gap) => gap.evidence) && (
                    <div className="mt-4 space-y-2 border-t pt-3">
                        {gaps
                            .filter((gap) => gap.evidence)
                            .slice(0, 3)
                            .map((gap) => (
                                <p key={gap.skillId} className="text-xs text-muted-foreground">
                                    <span className="font-medium text-foreground">
                                        {localizer.skill(gap.skillId, gap.skillLabel)}
                                    </span>{" "}
                                    — „{gap.evidence}“
                                </p>
                            ))}
                    </div>
                )}
            </section>
        </div>
    )
}
